import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../AuthContext';
import { getEffectiveAccountId, addScopeChangeListener } from '../../services/authorizationService';
import doctorService from '../../services/doctorService';
import locationService from '../../services/locationService';
import FileImportModal from '../../components/FileImportModal';
import { 
  Plus, 
  MagnifyingGlass, 
  MapPin, 
  User, 
  Phone, 
  Envelope,
  Pencil,
  Trash,
  X,
  Camera,
  Building,
  Upload
} from '@phosphor-icons/react';

export default function Directory() {
  const { currentUser } = useAuth();
  const [doctors, setDoctors] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [selectedSpecialty, setSelectedSpecialty] = useState('all');
  const [showDoctorModal, setShowDoctorModal] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editingDoctor, setEditingDoctor] = useState(null);
  const [editingLocation, setEditingLocation] = useState(null);

  // Doctor form state
  const [doctorForm, setDoctorForm] = useState({
    name: '',
    specialty: '',
    locationIds: [],
    phone: '',
    email: '',
    bio: '',
    profilePhoto: null
  });

  // Location form state
  const [locationForm, setLocationForm] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    phone: '',
    email: ''
  });

  // Deduplicate doctors by name and merge their locations
  const deduplicateDoctors = (doctors) => {
    const doctorMap = new Map();
    
    doctors.forEach(doctor => {
      const key = doctor.name.toLowerCase().trim();
      
      if (doctorMap.has(key)) {
        // Merge locations for duplicate doctors
        const existingDoctor = doctorMap.get(key);
        const existingLocationIds = existingDoctor.locationIds || (existingDoctor.locationId ? [existingDoctor.locationId] : []);
        const newLocationIds = doctor.locationIds || (doctor.locationId ? [doctor.locationId] : []);
        
        // Combine and deduplicate location IDs
        const combinedLocationIds = [...new Set([...existingLocationIds, ...newLocationIds])];
        
        // Merge other fields, preferring non-empty values
        const mergedDoctor = {
          ...existingDoctor,
          locationIds: combinedLocationIds,
          // Keep the most complete data for other fields
          phone: doctor.phone || existingDoctor.phone,
          email: doctor.email || existingDoctor.email,
          bio: doctor.bio || existingDoctor.bio,
          profilePhoto: doctor.profilePhoto || existingDoctor.profilePhoto,
          specialty: doctor.specialty || existingDoctor.specialty
        };
        
        doctorMap.set(key, mergedDoctor);
      } else {
        // Convert single locationId to locationIds array for consistency
        const locationIds = doctor.locationIds || (doctor.locationId ? [doctor.locationId] : []);
        doctorMap.set(key, {
          ...doctor,
          locationIds
        });
      }
    });
    
    return Array.from(doctorMap.values());
  };

  // Load doctors and locations
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const effectiveAccountId = getEffectiveAccountId();
      if (!effectiveAccountId) {
        console.log('No effective account ID, cannot load data');
        setDoctors([]);
        setLocations([]);
        return;
      }

      const [doctorsData, locationsData] = await Promise.all([
        doctorService.getDoctors(effectiveAccountId),
        locationService.getLocations(effectiveAccountId)
      ]);

      // Deduplicate doctors by name and merge their locations
      const deduplicatedDoctors = deduplicateDoctors(doctorsData);
      console.log('Original doctors count:', doctorsData.length);
      console.log('Deduplicated doctors count:', deduplicatedDoctors.length);
      
      setDoctors(deduplicatedDoctors);
      setLocations(locationsData);
    } catch (error) {
      console.error('Error loading directory data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load data on mount and account scope changes
  useEffect(() => {
    if (currentUser?.uid) {
      loadData();
    }
  }, [currentUser, loadData]);

  // Listen for account scope changes
  useEffect(() => {
    const unsubscribe = addScopeChangeListener((scopeData) => {
      console.log('Account scope changed in Directory:', scopeData);
      if (scopeData.effectiveAccountId && currentUser) {
        loadData();
      }
    });

    return unsubscribe;
  }, [currentUser, loadData]);

  // Handle doctor form submission
  const handleDoctorSubmit = async (e) => {
    e.preventDefault();
    
    // Validate that at least one location is selected
    if (doctorForm.locationIds.length === 0) {
      alert('Please select at least one location for the doctor.');
      return;
    }
    
    try {
      const effectiveAccountId = getEffectiveAccountId();
      if (!effectiveAccountId) {
        alert('No account scope set. Please select an account.');
        return;
      }

      if (editingDoctor) {
        const updatedDoctor = await doctorService.updateDoctor(editingDoctor.id, doctorForm, effectiveAccountId);
        setDoctors(prev => prev.map(doctor => 
          doctor.id === editingDoctor.id ? { ...doctor, ...updatedDoctor } : doctor
        ));
      } else {
        const newDoctor = await doctorService.createDoctor(doctorForm, effectiveAccountId);
        setDoctors(prev => [...prev, newDoctor]);
      }

      resetDoctorForm();
      setShowDoctorModal(false);
    } catch (error) {
      console.error('Error saving doctor:', error);
      alert('Failed to save doctor. Please try again.');
    }
  };

  // Handle location form submission
  const handleLocationSubmit = async (e) => {
    e.preventDefault();
    try {
      const effectiveAccountId = getEffectiveAccountId();
      if (!effectiveAccountId) {
        alert('No account scope set. Please select an account.');
        return;
      }

      if (editingLocation) {
        await locationService.updateLocation(editingLocation.id, locationForm, effectiveAccountId);
        setLocations(prev => prev.map(location => 
          location.id === editingLocation.id ? { ...location, ...locationForm } : location
        ));
      } else {
        const newLocation = await locationService.createLocation(locationForm, effectiveAccountId);
        setLocations(prev => [...prev, newLocation]);
      }

      resetLocationForm();
      setShowLocationModal(false);
    } catch (error) {
      console.error('Error saving location:', error);
      alert('Failed to save location. Please try again.');
    }
  };

  // Reset forms
  const resetDoctorForm = () => {
    setDoctorForm({
      name: '',
      specialty: '',
      locationIds: [],
      phone: '',
      email: '',
      bio: '',
      profilePhoto: null
    });
    setEditingDoctor(null);
  };

  const resetLocationForm = () => {
    setLocationForm({
      name: '',
      address: '',
      city: '',
      state: '',
      zipCode: '',
      phone: '',
      email: ''
    });
    setEditingLocation(null);
  };

  // Handle doctor deletion
  const handleDoctorDelete = async (doctorId) => {
    if (!window.confirm('Are you sure you want to delete this doctor?')) return;

    try {
      await doctorService.deleteDoctor(doctorId);
      setDoctors(prev => prev.filter(doctor => doctor.id !== doctorId));
    } catch (error) {
      console.error('Error deleting doctor:', error);
      alert('Failed to delete doctor. Please try again.');
    }
  };

  // Handle location deletion
  const handleLocationDelete = async (locationId) => {
    if (!window.confirm('Are you sure you want to delete this location?')) return;

    try {
      await locationService.deleteLocation(locationId);
      setLocations(prev => prev.filter(location => location.id !== locationId));
    } catch (error) {
      console.error('Error deleting location:', error);
      alert('Failed to delete location. Please try again.');
    }
  };

  // Handle edit doctor
  const handleEditDoctor = (doctor) => {
    setEditingDoctor(doctor);
    setDoctorForm({
      name: doctor.name || '',
      specialty: doctor.specialty || '',
      locationIds: doctor.locationIds || (doctor.locationId ? [doctor.locationId] : []),
      phone: doctor.phone || '',
      email: doctor.email || '',
      bio: doctor.bio || '',
      profilePhoto: doctor.profilePhoto || null // Preserve existing profile photo
    });
    setShowDoctorModal(true);
  };

  // Handle edit location
  const handleEditLocation = (location) => {
    setEditingLocation(location);
    setLocationForm({
      name: location.name || '',
      address: location.address || '',
      city: location.city || '',
      state: location.state || '',
      zipCode: location.zipCode || '',
      phone: location.phone || '',
      email: location.email || ''
    });
    setShowLocationModal(true);
  };

  // Filter doctors
  const filteredDoctors = doctors.filter(doctor => {
    const matchesSearch = doctor.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         doctor.specialty.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Handle both old single locationId and new locationIds array
    const doctorLocations = doctor.locationIds || (doctor.locationId ? [doctor.locationId] : []);
    const matchesLocation = selectedLocation === 'all' || doctorLocations.includes(selectedLocation);
    
    const matchesSpecialty = selectedSpecialty === 'all' || doctor.specialty === selectedSpecialty;
    
    return matchesSearch && matchesLocation && matchesSpecialty;
  });

  // Handle bulk import
  const handleBulkImport = async (importData) => {
    try {
      const effectiveAccountId = getEffectiveAccountId();
      if (!effectiveAccountId) {
        alert('No account scope set. Please select an account.');
        return;
      }

      const { locations: newLocations, doctors: newDoctors } = importData;
      
      // Create locations first
      const createdLocations = [];
      for (const locationData of newLocations) {
        const location = await locationService.createLocation(locationData, effectiveAccountId);
        createdLocations.push(location);
      }

      // Create location name to ID mapping
      const locationMap = new Map();
      createdLocations.forEach(location => {
        locationMap.set(location.name, location.id);
      });

      // Create doctors with proper location IDs
      const createdDoctors = [];
      for (const doctorData of newDoctors) {
        // Convert location names to location IDs
        const locationIds = (doctorData.locationNames || []).map(locationName => locationMap.get(locationName)).filter(Boolean);
        
        const doctorWithLocationIds = {
          ...doctorData,
          locationIds: locationIds
        };
        
        // Remove temporary locationNames field
        delete doctorWithLocationIds.locationNames;
        
        const doctor = await doctorService.createDoctor(doctorWithLocationIds, effectiveAccountId);
        createdDoctors.push(doctor);
      }

      // Update local state
      setLocations(prev => [...prev, ...createdLocations]);
      setDoctors(prev => [...prev, ...createdDoctors]);

      alert(`Successfully imported ${createdLocations.length} locations and ${createdDoctors.length} doctors!`);
    } catch (error) {
      console.error('Error during bulk import:', error);
      alert('Failed to import data. Please try again.');
    }
  };

  // Get unique specialties
  const specialties = [...new Set(doctors.map(doctor => doctor.specialty).filter(Boolean))];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading directory...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Directory</h2>
          <p className="text-gray-600">Manage doctors and practice locations</p>
        </div>
        <div className="flex space-x-3">
          <button 
            onClick={() => setShowImportModal(true)}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2"
          >
            <Upload className="w-5 h-5" />
            <span>Import CSV/Excel</span>
          </button>
          <button 
            onClick={() => {
              resetLocationForm();
              setShowLocationModal(true);
            }}
            className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors flex items-center space-x-2"
          >
            <Building className="w-5 h-5" />
            <span>Add Location</span>
          </button>
          <button 
            onClick={() => {
              resetDoctorForm();
              setShowDoctorModal(true);
            }}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
          >
            <Plus className="w-5 h-5" />
            <span>Add Doctor</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
            <div className="relative">
              <MagnifyingGlass className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input 
                type="text" 
                placeholder="Search doctors..." 
                value={searchQuery} 
                onChange={(e) => setSearchQuery(e.target.value)} 
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" 
              />
            </div>
          </div>

          {/* Location Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
            <select 
              value={selectedLocation} 
              onChange={(e) => setSelectedLocation(e.target.value)} 
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Locations</option>
              {locations.map(location => (
                <option key={location.id} value={location.id}>{location.name}</option>
              ))}
            </select>
          </div>

          {/* Specialty Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Specialty</label>
            <select 
              value={selectedSpecialty} 
              onChange={(e) => setSelectedSpecialty(e.target.value)} 
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Specialties</option>
              {specialties.map(specialty => (
                <option key={specialty} value={specialty}>{specialty}</option>
              ))}
            </select>
          </div>

          {/* Results Count */}
          <div className="flex items-end">
            <p className="text-sm text-gray-500">
              {filteredDoctors.length} doctor{filteredDoctors.length !== 1 ? 's' : ''} found
            </p>
          </div>
        </div>
      </div>

      {/* Locations Section */}
      {locations.length > 0 && (
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Practice Locations</h3>
            <p className="text-sm text-gray-500">Manage your practice locations</p>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {locations.map((location) => (
                <div key={location.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <Building className="w-8 h-8 text-blue-500" />
                      <div>
                        <h4 className="font-medium text-gray-900">{location.name}</h4>
                        <p className="text-sm text-gray-500">{location.city}, {location.state}</p>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <button 
                        onClick={() => handleEditLocation(location)}
                        className="text-blue-600 hover:text-blue-800 p-1"
                        title="Edit Location"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button 
                        onClick={() => handleLocationDelete(location.id)}
                        className="text-red-600 hover:text-red-800 p-1"
                        title="Delete Location"
                      >
                        <Trash className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div className="space-y-1 text-sm text-gray-600">
                    <p>{location.address}</p>
                    <p>{location.city}, {location.state} {location.zipCode}</p>
                    {location.phone && (
                      <p className="flex items-center">
                        <Phone className="w-4 h-4 mr-2" />
                        {location.phone}
                      </p>
                    )}
                    {location.email && (
                      <p className="flex items-center">
                        <Envelope className="w-4 h-4 mr-2" />
                        {location.email}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Doctors Grid */}
      <div className="bg-white rounded-lg shadow">
        {filteredDoctors.length === 0 ? (
          <div className="text-center py-12">
            <User className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No doctors found</h3>
            <p className="text-gray-500 mb-4">
              {doctors.length === 0 
                ? 'Add your first doctor to get started' 
                : 'Try adjusting your search or filters'
              }
            </p>
            <button 
              onClick={() => {
                resetDoctorForm();
                setShowDoctorModal(true);
              }}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Add Doctor
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-6">
            {filteredDoctors.map((doctor) => {
              // Handle both old single locationId and new locationIds array
              const doctorLocationIds = doctor.locationIds || (doctor.locationId ? [doctor.locationId] : []);
              const doctorLocations = doctorLocationIds.map(id => locations.find(loc => loc.id === id)).filter(Boolean);
              return (
                <div key={doctor.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  {/* Profile Photo */}
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="w-16 h-16 bg-gray-100 rounded-full overflow-hidden flex-shrink-0">
                      {doctor.profilePhoto ? (
                        <img 
                          src={doctor.profilePhoto} 
                          alt={doctor.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <User className="w-8 h-8 text-gray-400" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-gray-900 truncate">{doctor.name}</h3>
                      <p className="text-sm text-blue-600">{doctor.specialty}</p>
                      {doctorLocations.length > 0 && (
                        <div className="text-sm text-gray-500">
                          <div className="flex items-center mb-1">
                            <MapPin className="w-4 h-4 mr-1" />
                            <span className="font-medium">Locations:</span>
                          </div>
                          {doctorLocations.map((location, index) => (
                            <p key={location.id} className="ml-5 text-xs">
                              {location.name}
                            </p>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Contact Info */}
                  {(doctor.phone || doctor.email) && (
                    <div className="space-y-2 mb-4">
                      {doctor.phone && (
                        <p className="text-sm text-gray-600 flex items-center">
                          <Phone className="w-4 h-4 mr-2" />
                          {doctor.phone}
                        </p>
                      )}
                      {doctor.email && (
                        <p className="text-sm text-gray-600 flex items-center">
                          <Envelope className="w-4 h-4 mr-2" />
                          {doctor.email}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Bio */}
                  {doctor.bio && (
                    <p className="text-sm text-gray-600 mb-4 line-clamp-3">{doctor.bio}</p>
                  )}

                  {/* Actions */}
                  <div className="flex space-x-2">
                    <button 
                      onClick={() => handleEditDoctor(doctor)}
                      className="flex-1 bg-blue-600 text-white px-3 py-2 rounded-md hover:bg-blue-700 transition-colors flex items-center justify-center space-x-1 text-sm"
                    >
                      <Pencil className="w-4 h-4" />
                      <span>Edit</span>
                    </button>
                    <button 
                      onClick={() => handleDoctorDelete(doctor.id)}
                      className="bg-red-600 text-white px-3 py-2 rounded-md hover:bg-red-700 transition-colors flex items-center justify-center"
                    >
                      <Trash className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Doctor Modal */}
      {showDoctorModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {editingDoctor ? 'Edit Doctor' : 'Add Doctor'}
              </h3>
              <button 
                onClick={() => {
                  setShowDoctorModal(false);
                  resetDoctorForm();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleDoctorSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
                  <input
                    type="text"
                    required
                    value={doctorForm.name}
                    onChange={(e) => setDoctorForm(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Dr. John Smith"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Specialty *</label>
                  <input
                    type="text"
                    required
                    value={doctorForm.specialty}
                    onChange={(e) => setDoctorForm(prev => ({ ...prev, specialty: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Cardiology, Oncology, etc."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Locations *</label>
                  <div className="space-y-2 max-h-32 overflow-y-auto border border-gray-300 rounded-md p-2">
                    {locations.map(location => (
                      <label key={location.id} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={doctorForm.locationIds.includes(location.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setDoctorForm(prev => ({
                                ...prev,
                                locationIds: [...prev.locationIds, location.id]
                              }));
                            } else {
                              setDoctorForm(prev => ({
                                ...prev,
                                locationIds: prev.locationIds.filter(id => id !== location.id)
                              }));
                            }
                          }}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">{location.name}</span>
                      </label>
                    ))}
                  </div>
                  {doctorForm.locationIds.length === 0 && (
                    <p className="text-sm text-red-500 mt-1">Please select at least one location</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                  <input
                    type="tel"
                    value={doctorForm.phone}
                    onChange={(e) => setDoctorForm(prev => ({ ...prev, phone: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="(555) 123-4567"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                  <input
                    type="email"
                    value={doctorForm.email}
                    onChange={(e) => setDoctorForm(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="doctor@example.com"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Bio</label>
                  <textarea
                    value={doctorForm.bio}
                    onChange={(e) => setDoctorForm(prev => ({ ...prev, bio: e.target.value }))}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Brief bio about the doctor..."
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Profile Photo</label>
                  <div className="flex items-center space-x-4">
                    <div className="w-20 h-20 bg-gray-100 rounded-full overflow-hidden flex-shrink-0">
                      {doctorForm.profilePhoto ? (
                        <img 
                          src={doctorForm.profilePhoto instanceof File ? URL.createObjectURL(doctorForm.profilePhoto) : doctorForm.profilePhoto} 
                          alt="Preview"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Camera className="w-8 h-8 text-gray-400" />
                        </div>
                      )}
                    </div>
                    <div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => setDoctorForm(prev => ({ ...prev, profilePhoto: e.target.files[0] }))}
                        className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                      />
                      <p className="text-xs text-gray-500 mt-1">JPG, PNG up to 10MB</p>
                      {doctorForm.profilePhoto && (
                        <button
                          type="button"
                          onClick={() => setDoctorForm(prev => ({ ...prev, profilePhoto: null }))}
                          className="mt-2 text-sm text-red-600 hover:text-red-800"
                        >
                          Remove Photo
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowDoctorModal(false);
                    resetDoctorForm();
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  {editingDoctor ? 'Update Doctor' : 'Add Doctor'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Location Modal */}
      {showLocationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {editingLocation ? 'Edit Location' : 'Add Location'}
              </h3>
              <button 
                onClick={() => {
                  setShowLocationModal(false);
                  resetLocationForm();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleLocationSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Location Name *</label>
                  <input
                    type="text"
                    required
                    value={locationForm.name}
                    onChange={(e) => setLocationForm(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Main Office, Downtown Clinic, etc."
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Address *</label>
                  <input
                    type="text"
                    required
                    value={locationForm.address}
                    onChange={(e) => setLocationForm(prev => ({ ...prev, address: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="123 Main Street"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">City *</label>
                  <input
                    type="text"
                    required
                    value={locationForm.city}
                    onChange={(e) => setLocationForm(prev => ({ ...prev, city: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="New York"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">State *</label>
                  <input
                    type="text"
                    required
                    value={locationForm.state}
                    onChange={(e) => setLocationForm(prev => ({ ...prev, state: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="NY"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">ZIP Code *</label>
                  <input
                    type="text"
                    required
                    value={locationForm.zipCode}
                    onChange={(e) => setLocationForm(prev => ({ ...prev, zipCode: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="10001"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                  <input
                    type="tel"
                    value={locationForm.phone}
                    onChange={(e) => setLocationForm(prev => ({ ...prev, phone: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="(555) 123-4567"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                  <input
                    type="email"
                    value={locationForm.email}
                    onChange={(e) => setLocationForm(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="info@example.com"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowLocationModal(false);
                    resetLocationForm();
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  {editingLocation ? 'Update Location' : 'Add Location'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Import Modal */}
      <FileImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImport={handleBulkImport}
      />
    </div>
  );
}
